import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { ResponsiveLogo } from '../components/ThemeLogo';
import PublicHeader from '../components/PublicHeader';
import {
  Moon, Sun, ArrowRight, Play, ChevronDown, Check, X,
  Lightbulb, Briefcase, Users, Rocket, Eye,
  Linkedin, Twitter, Youtube, Search, Bot,
  BarChart3, MessageCircle, ClipboardCheck, Globe,
} from 'lucide-react';

/* ───── Animation ───── */
const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-60px' }, transition: { duration: 0.5 } };
const stagger = { initial: 'hidden', whileInView: 'visible', viewport: { once: true, margin: '-40px' }, variants: { visible: { transition: { staggerChildren: 0.08 } } } };
const childFade = { variants: { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } } };

/* ───── Data ───── */
const painPoints = [
  { img: '/landing/pain-brain.png', title: 'Too many ideas', desc: 'Everything is in your head.' },
  { img: '/landing/pain-noise.png', title: 'Too much noise', desc: 'Emails, chats, reports, calls.' },
  { img: '/landing/pain-powerpoint.png', title: 'Strategy lives in PowerPoint', desc: 'Nice decks. No real impact.' },
  { img: '/landing/pain-nofocus.png', title: 'No focus', desc: 'Every priority feels urgent.' },
  { img: '/landing/pain-meetings.png', title: 'Meetings about meetings', desc: 'Lots of talk. Little action.' },
  { img: '/landing/pain-unfinished.png', title: 'Nothing gets finished', desc: 'Good intentions. Zero follow-through.' },
];

const personas = [
  { icon: Bot, name: 'The Strategist', desc: 'Sees the big picture and connects the dots.', color: 'text-teal-600' },
  { icon: Search, name: 'The Challenger', desc: 'Questions your assumptions. Pushes you further.', color: 'text-orange-500' },
  { icon: BarChart3, name: 'The CFO', desc: 'Brings numbers. Tests feasibility and ROI.', color: 'text-blue-500' },
  { icon: MessageCircle, name: 'The Customer', desc: "Represents your customer's voice. Brings reality.", color: 'text-pink-500' },
  { icon: ClipboardCheck, name: 'The Execution Coach', desc: 'Turns insights into action.', color: 'text-emerald-500' },
];

const builtFor = [
  { icon: Lightbulb, label: 'Founders' },
  { icon: Briefcase, label: 'Consultants' },
  { icon: Users, label: 'SME Leadership' },
  { icon: Rocket, label: 'Innovation Teams' },
  { icon: Eye, label: 'Visual Thinkers' },
];

const hw = "font-['Caveat'] font-bold"; // handwritten utility

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const dest = isAuthenticated ? '/dashboard' : '/register';

  return (
    <div className="min-h-screen bg-[#FFF9F2] dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 overflow-x-hidden">

      {/* ══ NAVBAR ══ */}
      <PublicHeader />

      {/* ══ HERO ══ */}
      <section className="pt-12 pb-16 sm:pt-16 sm:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight">
              Do you have a business issue<br />
              <span className={`${hw} text-red-500 text-4xl sm:text-5xl lg:text-6xl`}>Odapto helps you think it through and diagnoses what matters</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-600 dark:text-zinc-400 max-w-lg leading-relaxed">
              Your guided online interview turns ideas into actions.<br />
              A strategy consultant in your pocket.<br />
              Without the suits. Without the slide deck.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={dest}>
                <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white rounded-full px-7 py-6 text-base gap-2 shadow-lg">
                  <Play className="w-4 h-4 fill-white" /> Start 10-minute interview
                </Button>
              </Link>
              <Button variant="outline" size="lg" className="rounded-full px-7 py-6 text-base border-2">See demo</Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-5 text-xs text-zinc-500 dark:text-zinc-400">
              <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-teal-500" /> No credit card</span>
              <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-teal-500" /> Beta access</span>
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-teal-500" /> Built for founders & consultants</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <img src="/landing/hero-founder.png?v=2" alt="Founder thinking" className="w-full mx-auto dark:hidden" />
            <img src="/landing/hero-founder-dark.png?v=2" alt="Founder thinking" className="w-full mx-auto hidden dark:block" />
          </motion.div>
        </div>
      </section>

      {/* ══ SOUND FAMILIAR? ══ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-white dark:bg-zinc-900/50 border-y border-zinc-200 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-extrabold text-center mb-4">
            Sound <span className={`${hw} text-red-500 text-4xl sm:text-5xl`}>familiar?</span>
          </motion.h2>
          <motion.div {...stagger} className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8">
            {painPoints.map((p) => (
              <motion.div key={p.title} {...childFade} className="flex flex-col items-center text-center">
                <img src={p.img} alt={p.title} className="w-20 h-20 object-contain mb-3" />
                <p className="font-bold text-sm">{p.title}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.p {...fadeUp} className="mt-14 text-center text-lg font-semibold">
            Smart people don't need more tools. <span className={`${hw} text-red-500 text-2xl underline decoration-wavy decoration-red-300`}>They need clarity.</span>
          </motion.p>
        </div>
      </section>

      {/* ══ THINK LESS CHAOS. MORE STRUCTURE ══ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-extrabold text-center mb-16">
            Think less chaos. <span className={`${hw} text-red-500 text-4xl sm:text-5xl`}>More structure.</span>
          </motion.h2>
          <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 items-start">
            {[
              { num: '1', title: 'Get interviewed', desc: 'MrCanvas asks the questions a sharp consultant would ask.', img: '/landing/ai-interview.png' },
              { num: '2', title: 'Get the right frameworks', desc: 'No blank-page syndrome. AI picks the 1–5 canvases that matter.', img: '/landing/frameworks-grid.png' },
              { num: '3', title: 'Flip strategy into execution', desc: 'Turn generic frameworks into your company-specific plan.', img: '/landing/flip-execution.png' },
            ].map((step, i) => (
              <React.Fragment key={step.num}>
                <motion.div {...fadeUp} className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-6 shadow-sm hover:shadow-md transition-shadow">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-500 text-white font-bold text-sm mb-4">{step.num}</span>
                  <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">{step.desc}</p>
                  <img src={step.img} alt={step.title} className="w-full h-48 object-contain" />
                </motion.div>
                {i < 2 && (
                  <div className="hidden md:flex items-center justify-center">
                    <img src="/landing/arrow.png" alt="arrow" className="w-10 h-10 dark:invert" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ══ VISUAL BOARDS ══ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-white dark:bg-zinc-900/50 border-y border-zinc-200 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
              Visual boards, deeper thinking,<br /><span className={`${hw} text-red-500 text-4xl sm:text-5xl`}>better decisions.</span>
            </h2>
            <ul className="mt-8 space-y-3">
              {['Boards & cards', 'Flip cards (general \u2192 your world)', 'Add notes, links, tasks', 'MrCanvas guidance when you need it', 'Review. Improve. Repeat.'].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" />{item}</li>
              ))}
            </ul>
            <div className="mt-8 bg-amber-100 dark:bg-amber-900/30 rounded-xl p-5 border-l-4 border-amber-400 inline-block rotate-[-1deg]">
              <p className={`${hw} text-lg text-zinc-800 dark:text-zinc-200`}>From strategy to execution.</p>
              <p className={`${hw} text-lg text-zinc-800 dark:text-zinc-200`}>All in one place.</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <ResponsiveLogo className="h-5 w-auto" />
                <span className="text-sm font-semibold">Business Model Canvas</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {['Key Partners', 'Key Activities', 'Value Propositions', 'Customer Relationships', 'Customer Segments'].map((h) => (
                  <div key={h} className="bg-zinc-100 dark:bg-zinc-800 rounded p-2 text-[10px] font-semibold text-center">{h}</div>
                ))}
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="bg-zinc-50 dark:bg-zinc-800/50 rounded p-2 h-12 flex items-center justify-center">
                    <div className={`w-6 h-6 rounded ${i % 3 === 0 ? 'bg-teal-200 dark:bg-teal-800' : i % 3 === 1 ? 'bg-orange-200 dark:bg-orange-800' : 'bg-pink-200 dark:bg-pink-800'}`} />
                  </div>
                ))}
              </div>
              <div className="mt-3 text-center">
                <span className={`${hw} text-red-500 text-lg`}>FLIP!</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ WHY BUY EXPENSIVE POWERPOINTS? ══ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-extrabold mb-14">
            Why buy expensive<br /><span className={`${hw} text-red-500 text-4xl sm:text-5xl`}>PowerPoints?</span>
          </motion.h2>
          <motion.div {...fadeUp} className="grid md:grid-cols-[1fr_auto_1fr] gap-6 items-start">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-6">
              <h3 className="font-bold mb-4 text-zinc-500">Traditional consulting</h3>
              {['Expensive', 'Expertise sit in individual people', 'Quality varies by consultant/team', 'One off project', 'Usually you pay for the hours not the outcome'].map((item) => (
                <div key={item} className="flex items-center gap-2 py-2 text-sm"><X className="w-4 h-4 text-red-400 shrink-0" />{item}</div>
              ))}
            </div>
            <div className="flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-extrabold text-zinc-500 text-lg">VS</div>
            </div>
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border-2 border-teal-400 p-6">
              <h3 className="font-bold mb-4 text-teal-600">Odapto</h3>
              {['Affordable', 'Immediate', 'Personalized to your context', 'Focused on outcome', 'Self-action & review'].map((item) => (
                <div key={item} className="flex items-center gap-2 py-2 text-sm">
                  <Check className="w-4 h-4 text-teal-500 shrink-0" />
                  <span className={item === 'Focused on outcome' ? 'font-bold text-teal-600' : ''}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="mt-8 flex justify-end">
            <div className="bg-amber-100 dark:bg-amber-900/30 rounded-xl p-5 border-l-4 border-amber-400 rotate-[1deg]">
              <p className={`${hw} text-lg`}>Better focus.</p>
              <p className={`${hw} text-lg`}>More reflection.</p>
              <p className={`${hw} text-lg text-red-500`}>Real progress.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ WE'RE JUST GETTING STARTED ══ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-white dark:bg-zinc-900/50 border-y border-zinc-200 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold">
              We're just<br /><span className={`${hw} text-red-500 text-4xl sm:text-5xl`}>getting started.</span>
            </h2>
            <p className="mt-4 text-zinc-600 dark:text-zinc-400 max-w-lg">
              Today: guided strategy interview, smart canvases, execution workspace.<br />
              Tomorrow: Multi-agent strategic sparring.
            </p>
          </motion.div>
          <motion.div {...stagger} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {personas.map((p) => (
              <motion.div key={p.name} {...childFade} className="bg-[#FFF9F2] dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-5 text-center hover:shadow-md transition-shadow">
                <p.icon className={`w-10 h-10 mx-auto mb-3 ${p.color}`} />
                <h4 className="font-bold text-sm mb-1">{p.name}</h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══ BUILT FOR PEOPLE WHO THINK ══ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl font-extrabold mb-14">
            Think better. Decide faster.<br /><span className={`${hw} text-red-500 text-4xl sm:text-5xl`}>and build it yourself.</span>
          </motion.h2>
          <motion.div {...stagger} className="flex flex-wrap justify-center gap-10">
            {builtFor.map((b) => (
              <motion.div key={b.label} {...childFade} className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 flex items-center justify-center shadow-sm">
                  <b.icon className="w-7 h-7 text-teal-600" />
                </div>
                <span className="text-sm font-semibold">{b.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══ FINAL CTA ══ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[#1E404B] text-white">
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-10">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
              Stop buying advice.<br />
              <span className={`${hw} text-amber-300 text-4xl sm:text-5xl`}>Start building strategy.</span>
            </h2>
          </motion.div>
          <motion.div {...fadeUp} className="flex flex-wrap gap-4">
            <Link to={dest}>
              <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white rounded-full px-7 py-6 text-base gap-2 shadow-lg">
                <Play className="w-4 h-4 fill-white" /> Start interview
              </Button>
            </Link>
            <Link to={dest}>
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-7 py-6 text-base">
                Explore workspace
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="bg-[#162F38] text-zinc-400 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8 mb-12">
            <div className="col-span-2 sm:col-span-1">
              <ResponsiveLogo className="w-auto brightness-200" style={{ height: '2.75rem' }} />
            </div>
            {[
              { title: 'Product', links: [
                { label: 'Features', href: '#' },
                { label: 'Templates', href: '#' },
                { label: 'MrCanvas', href: '#' },
                { label: 'Integrations', href: '#' },
              ]},
              { title: 'Resources', links: [
                { label: 'Blog', href: '/blog' },
                { label: 'Webinars', href: '#' },
                { label: 'Help Center', href: '#' },
                { label: 'Community', href: '#' },
              ]},
              { title: 'Company', links: [
                { label: 'About us', href: '/about-us' },
                { label: 'Careers', href: '#' },
                { label: 'Contact', href: '/contact' },
              ]},
              { title: 'Legal', links: [
                { label: 'Privacy', href: '/privacy' },
                { label: 'Terms', href: '/terms' },
                { label: 'Fair Use', href: '/fair-use' },
              ]},
            ].map((col) => (
              <div key={col.title}>
                <h4 className="text-white font-semibold text-sm mb-3">{col.title}</h4>
                <ul className="space-y-2">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.href.startsWith('/') ? (
                        <Link to={l.href} className="text-xs hover:text-white transition-colors">{l.label}</Link>
                      ) : (
                        <span className="text-xs hover:text-white transition-colors cursor-pointer">{l.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-700 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs">&copy; 2026 Odapto. All rights reserved.</span>
            <div className="flex gap-4">
              <Linkedin className="w-4 h-4 hover:text-white cursor-pointer transition-colors" />
              <Twitter className="w-4 h-4 hover:text-white cursor-pointer transition-colors" />
              <Youtube className="w-4 h-4 hover:text-white cursor-pointer transition-colors" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
